import type { PublicFilterWithDates } from '@/entities/post/api';
import type { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter/public-filter-constants';
import type { QueryDate } from '@/entities/query/query-types';
import { socialApiClient } from '@/shared/api-client/social-api-client';
import { getCountryName } from '@/shared/utils/get-country-name';

import type { PublicFilter } from '@/features/(insight)/shared/public-filter/use-public-filter';
import {
  type DemographicStat,
  type Hashtag,
  type Keyword,
  type Location,
  type Profile,
} from '../post/models';
import type { InsightCommonFetchParams, InsightCommonFetchParamsWithPrivateFilter } from './insight-types';
import { buildInsightReqBody, buildPublicFilterWithDates } from './insight-utils';

export type PerformanceOverview = {
  count: number;
  view: number;
  like: number;
  comment: number;
  shared: number;
  platform: SOURCE_PLATFORM_TYPE;
};

type PerformanceOverviewResponse = {
  stats: Array<PerformanceOverview>;
};

export async function fetchPerformanceOverview(
  {
    queryId,
    startDate,
    endDate,
    publicFilter,
  }: {
    queryId: string;
    startDate: string;
    endDate: string;
    publicFilter: PublicFilter;
  },
  signal?: AbortSignal,
) {
  const publicFilterBody: PublicFilterWithDates = buildPublicFilterWithDates(
    publicFilter,
    startDate,
    endDate,
  );

  const response = await socialApiClient.post<PerformanceOverviewResponse>(
    `queries/${queryId}/insight/performance/overview`,
    {
      json: {
        filter: {
          public: publicFilterBody,
        },
      },
      signal,
    },
  );

  const data = await response.json();

  // Transform the response data for better DX
  return data.stats;
}

export interface TrendData {
  date: QueryDate;
  comment: number;
  count: number;
  like: number;
  shared: number;
  view: number;
}

export interface TrendDataset {
  platform: SOURCE_PLATFORM_TYPE;
  data: TrendData[];
  count: number;
}

export interface PerformanceTrendResponse {
  trends: TrendDataset[];
}

export async function fetchPerformanceTrend(
  {
    queryId,
    startDate,
    endDate,
    period,
    publicFilter,
  }: {
    queryId: string;
    startDate: string;
    endDate: string;
    period: 'monthly' | 'weekly' | 'daily';
    publicFilter: PublicFilter;
  },
  signal?: AbortSignal,
) {
  const dateUnit = period === 'daily' ? 'day' : period === 'weekly' ? 'week' : 'month';
  const publicFilterBody: PublicFilterWithDates = buildPublicFilterWithDates(
    publicFilter,
    startDate,
    endDate,
  );

  const response = await socialApiClient.post<PerformanceTrendResponse>(
    `queries/${queryId}/insight/performance`,
    {
      json: {
        date_unit: dateUnit,
        filter: {
          public: publicFilterBody,
        },
      },
      signal,
    },
  );

  const data = await response.json();

  const getTotalStats = (datasets: TrendDataset[]) => {
    return datasets.reduce(
      (acc, curr) => {
        return {
          count: acc.count + curr.data.reduce((acc, curr) => acc + curr.count, 0),
          view: acc.view + curr.data.reduce((acc, curr) => acc + curr.view, 0),
          likes: acc.likes + curr.data.reduce((acc, curr) => acc + curr.like, 0),
          comments: acc.comments + curr.data.reduce((acc, curr) => acc + curr.comment, 0),
          shares: acc.shares + curr.data.reduce((acc, curr) => acc + curr.shared, 0),
          engagements:
            acc.engagements +
            curr.data.reduce((acc, curr) => acc + curr.like + curr.comment + curr.shared, 0),
        };
      },
      { count: 0, view: 0, likes: 0, comments: 0, shares: 0, engagements: 0 },
    );
  };

  const totalStats = getTotalStats(data.trends);

  return {
    totalVolume: totalStats.count,
    totalViews: totalStats.view,
    totalLikes: totalStats.likes,
    totalComments: totalStats.comments,
    totalShares: totalStats.shares,
    totalEngagements: totalStats.engagements,
    trends: data.trends,
  };
}

export type ProfileResponse = {
  count: number;
  profiles: Array<{
    author_id: string;
    username: string;
    nickname?: string;
    profile_image_url: string;
    follower_count: number;
    count: number;
    like: number;
    comment: number;
    shared: number;
    view: number;
    platform: SOURCE_PLATFORM_TYPE;
  }>;
};

// Base profiles fetcher - exported for reuse
export async function fetchProfiles(
  params: InsightCommonFetchParamsWithPrivateFilter,
  signal?: AbortSignal,
): Promise<{
  count: number;
  profiles: Profile[];
}> {
  const { queryId, startDate, endDate, limit = 100, publicFilter, filter } = params;
  const requestBody = buildInsightReqBody(startDate, endDate, limit, publicFilter, filter);

  const response = await socialApiClient
    .post(`queries/${queryId}/insight/people/profiles`, {
      json: requestBody,
      signal,
    })
    .json<ProfileResponse>();

  return {
    profiles: response.profiles.map((p) => ({
      authorId: p.author_id,
      username: p.username,
      nickname: p.nickname,
      profileImageUrl: p.profile_image_url,
      followerCount: p.follower_count,
      postCount: p.count,
      totalLikes: p.like,
      totalComments: p.comment,
      totalShares: p.shared,
      totalViews: p.view,
      platform: p.platform,
    })),
    count: response.count,
  };
}

export async function fetchKeywords(
  {
    queryId,
    startDate,
    endDate,
    limit = 100,
    publicFilter,
  }: InsightCommonFetchParamsWithPrivateFilter,
  signal?: AbortSignal,
) {
  const response = await socialApiClient
    .post(`queries/${queryId}/insight/conversation/related/keywords`, {
      json: buildInsightReqBody(startDate, endDate, limit, publicFilter),
      signal,
    })
    .json<{ keywords: Array<Keyword> }>();
  return response.keywords;
}

export async function fetchHashtags(
  {
    queryId,
    startDate,
    endDate,
    limit = 100,
    publicFilter,
  }: InsightCommonFetchParamsWithPrivateFilter,
  signal?: AbortSignal,
) {
  const response = await socialApiClient
    .post(`queries/${queryId}/insight/conversation/related/hashtags`, {
      json: buildInsightReqBody(startDate, endDate, limit, publicFilter),
      signal,
    })
    .json<{ hashtags: Array<Hashtag> }>();
  return response.hashtags;
}

type DemographicsStatResponse = {
  name: string;
  count: number;
  like: number;
  comment: number;
  shared: number;
  view: number;
};

const transformDemographicsStatResponse = (
  response: DemographicsStatResponse,
): DemographicStat => ({
  name: response.name,
  postCount: response.count,
  totalLikes: response.like,
  totalComments: response.comment,
  totalShares: response.shared,
  totalViews: response.view,
});

export async function fetchDemographics(
  { queryId, startDate, endDate, publicFilter }: InsightCommonFetchParams,
  signal?: AbortSignal,
): Promise<{
  gender: DemographicStat[];
  race: DemographicStat[];
  age: DemographicStat[];
}> {
  const requestBody = buildInsightReqBody(startDate, endDate, 100, publicFilter);
  const response = await socialApiClient
    .post(`queries/${queryId}/insight/people/demographics`, {
      json: requestBody,
      signal,
    })
    .json<{
      gender: Array<DemographicsStatResponse>;
      race: Array<DemographicsStatResponse>;
      age: Array<DemographicsStatResponse>;
    }>();

  return {
    gender: response.gender.map(transformDemographicsStatResponse),
    race: response.race.map(transformDemographicsStatResponse),
    age: response.age.map(transformDemographicsStatResponse),
  };
}

export async function fetchLocations(
  { queryId, startDate, endDate, limit = 100, publicFilter }: InsightCommonFetchParams,
  signal?: AbortSignal,
): Promise<Location[]> {
  const response = await socialApiClient
    .post(`queries/${queryId}/insight/people/locations`, {
      json: buildInsightReqBody(startDate, endDate, limit, publicFilter),
      signal,
    })
    .json<{
      locations: Array<{
        name: string;
        count: number;
        like: number;
        comment: number;
        shared: number;
        view: number;
      }>;
    }>();

  const result = response.locations.map((l) => ({
    name: getCountryName(l.name),
    code: l.name,
    postCount: l.count,
    totalLikes: l.like,
    totalComments: l.comment,
    totalShares: l.shared,
    totalViews: l.view,
  }));

  return result;
}
